import { VuexModule, Module, Mutation, Action, getModule, MutationAction } from 'vuex-module-decorators';
import store from '@/store';
import { CreateBugTrackerIssueRequest, BugTrackerCategoryViewObject, BugTrackerIssueDetailsViewModel, BugTrackerIssueSummaryViewModel, BugTrackerIssueViewObject, BugTrackerPriority, BugTrackerStatus, UpdateBugTrackerIssueRequest, BugTrackerIssueReactionRequest, BugTrackerIssueAddCommentRequest, BugTrackerIssueRemoveCommentRequest, BugTrackerIssueUpdateCommentRequest } from '@/types/apiServerContract';
import { BugTrackerApi } from '@/services/api/api.bugtracker';
import { UserModule } from '../user/user.store';
import bus from '@/core/bus';
import { SignalREvents } from '@/core/constants';

@Module({ namespaced: true, dynamic: true, store, name: 'BugTrackerModule' })
class BugTrackerState extends VuexModule {
    public latestIssues: BugTrackerIssueViewObject[] = [];
    public searchResults: BugTrackerIssueViewObject[] = [];
    public categories: BugTrackerCategoryViewObject[] = [];
    public summary: BugTrackerIssueSummaryViewModel | null = null;
    public newIssue: BugTrackerIssueViewObject | null = null;
    public details: BugTrackerIssueDetailsViewModel | null = null;

    @Mutation
    clearNewIssue() {
        this.newIssue = null;
    }

    @Mutation
    clearDetails() {
        this.details = null;
    }

    @Mutation
    clearSearchResults() {
        this.searchResults = [];
    }

    @Mutation
    handleIssueCreated(issue: BugTrackerIssueViewObject) {
        if (this.latestIssues.length >= 5) {
            this.latestIssues.pop();
        }
        this.latestIssues.unshift(issue);

        if (this.summary) {
            this.summary.totalIssues++;

            if (UserModule.user?.id === issue.createdBy) {
                this.summary.submittedIssues++;
                this.newIssue = issue;
            }
        }
    }

    @Mutation
    handleIssueDetailsUpdated(details: BugTrackerIssueDetailsViewModel) {
        if (this.details?.issue.id === details.issue.id) {
            this.details = details;
        }

        const latestIssue = this.latestIssues.find(x => x.id === details.issue.id);
        if (latestIssue) {
            Object.assign(latestIssue, details.issue);
        }

        const searchResult = this.searchResults.find(x => x.id === details.issue.id);
        if (searchResult) {
            Object.assign(searchResult, details.issue);
        }
    }

    @Mutation
    handleIssueRemoved(id: number) {
        const latestIssueIndex = this.latestIssues.findIndex(x => x.id === id);
        this.latestIssues.splice(latestIssueIndex, 1);
        const searchResultIndex = this.searchResults.findIndex(x => x.id === id);
        this.searchResults.splice(searchResultIndex, 1);
        if (this.details?.issue.id === id) {
            bus.emit(SignalREvents.BugTrackerIssueRemoved, { id: id, callback: () => this.details = null });
        }
    }

    @Action
    async createIssue(request: CreateBugTrackerIssueRequest) {
        const result = await BugTrackerApi.CreateIssue(request);
        this.handleIssueCreated(result.issue);
    }

    @MutationAction({ mutate: ['latestIssues'], rawError: true })
    async getLatestIssues() {
        const result = await BugTrackerApi.GetLatestIssues();
        return { latestIssues: result.issues };
    }

    @MutationAction({ mutate: ['categories'], rawError: true })
    async getCategories() {
        const result = await BugTrackerApi.GetCategories();
        return { categories: result.categories };
    }

    @MutationAction({ mutate: ['summary'], rawError: true })
    async getIssuesSummary() {
        const result = await BugTrackerApi.GetIssuesSummary();
        return { summary: result };
    }

    @MutationAction({ mutate: ['details'], rawError: true })
    async getIssueDetails(id: number) {
        const result = await BugTrackerApi.GetIssueDetails(id);
        return { details: result };
    }

    @MutationAction({ mutate: ['details'], rawError: true })
    async issueReaction(request: BugTrackerIssueReactionRequest) {
        const result = await BugTrackerApi.IssueReaction(request);
        return { details: result };
    }

    @MutationAction({ mutate: ['details'], rawError: true })
    async addComment(request: BugTrackerIssueAddCommentRequest) {
        const result = await BugTrackerApi.AddComment(request);
        return { details: result };
    }

    @MutationAction({ mutate: ['details'], rawError: true })
    async updateComment(request: BugTrackerIssueUpdateCommentRequest) {
        const result = await BugTrackerApi.UpdateComment(request);
        return { details: result };
    }

    @MutationAction({ mutate: ['details'], rawError: true })
    async removeComment(request: BugTrackerIssueRemoveCommentRequest) {
        const result = await BugTrackerApi.RemoveComment(request);
        return { details: result };
    }

    @Action({ rawError: true })
    async updateIssue(request: UpdateBugTrackerIssueRequest) {
        const result = await BugTrackerApi.UpdateIssue(request);
        this.handleIssueDetailsUpdated(result);
        if (request.triggerSummariesBroadcast) {
            this.getIssuesSummary();
        }
    }

    @Action({ rawError: true })
    async removeIssue(issue: BugTrackerIssueViewObject) {
        await BugTrackerApi.RemoveIssue(issue.id);
        this.handleIssueRemoved(issue.id);
        this.getIssuesSummary();
    }

    @MutationAction({ mutate: ['searchResults'], rawError: true })
    async search(request: {
        onlyOpenIssues: boolean | null,
        onlyOwnIssues: boolean | null,
        assignedToSelf: boolean | null,
        term: string | null,
        categoryId: number | null,
        realmId: number | null,
        startDate: Date | null,
        endDate: Date | null,
        status: BugTrackerStatus | null,
        priority: BugTrackerPriority | null,
        page: number | null
    }) {
        const result = await BugTrackerApi.SearchIssues(
            request.onlyOpenIssues,
            request.onlyOwnIssues,
            request.assignedToSelf,
            request.term,
            request.categoryId,
            request.realmId,
            request.startDate,
            request.endDate,
            request.status,
            request.priority,
            request.page);
        return { searchResults: result.issues };
    }

    @Mutation
    Reset() { }

    @Action({ rawError: true })
    Clear() {
        this.Reset();
    }

    @Action({ rawError: true })
    async Init() {
        this.getLatestIssues();
    }
}

const BugTrackerModule = getModule(BugTrackerState);

export { BugTrackerModule };
